import { orderFlowCloseReason, useOrderFlowContext } from '@src/app/order-flow-context';
import { useOrderInfoContext } from '@src/app/order-info-context';
import { useEffect, useRef, useState } from 'react';
import Button from '../button/Button';
import VariationGroup from './VariationGroup';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { BREAKPOINT_DESKTOP, BREAKPOINT_TABLET } from '@src/hooks/use-breakpoint';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Svg from '../svg/Svg';
import { BasedModel, getModifiers } from '@src/helpers/getModifiers';

export interface VariationItemModel {
  variantId: number;
  productCode: string;
  name: string;
  minQuantity: number;
  maxQuantity: number;
  note: string;
  src: string;
  batchQuantity?: number;
  quantityRemaining?: number;
  bidAllocationProduct?: boolean;
}

export interface VariationGroupModel {
  groupId: number;
  code: string;
  name: string;
  tooltip: string;
  src: string;
  text: string;
  items: VariationItemModel[];
  slug?: string;
}

export interface VariationPickerModel extends BasedModel {
  title: string;
  quantityQuestion: string;
  items: VariationGroupModel[];
  ctaText: string;
  tabletColumnCount: number;
  desktopColumnCount: number;
  collectingFromPostcodeText: string;
  changePostcodeText: string;
  quantityRemainingText?: string;
}

interface Props {
  data: VariationPickerModel;
}

const VariationPicker = ({ data }: Props) => {
  const styleModifiers = getModifiers(data, 'fm-o-variation-switcher');
  const { groupId, variantId, quantity } = useOrderInfoContext();
  const { handleContinue, handleClose } = useOrderFlowContext();
  const [variationSelected, setVariationSelected] = useState<VariationItemModel>();
  const swiperRef = useRef<HTMLDivElement>(null);

  const hasGroupId = !!groupId && groupId > 0;
  const hasVariantId = !!variantId && variantId > 0;

  const handleVariationChange = (item: VariationItemModel | undefined) => {
    setVariationSelected(item);
  };

  useEffect(() => {
    const swiperEl = swiperRef.current;

    if (!swiperEl || data.items.length <= 3) {
      return;
    }

    const swiper = new Swiper(swiperEl, {
      modules: [Navigation, Pagination],
      slidesPerView: 1,
      spaceBetween: 20,
      slidesPerGroup: 1,
      breakpoints: {
        [BREAKPOINT_TABLET]: {
          slidesPerView: 2,
          spaceBetween: 20,
          slidesPerGroup: 2,
        },
        [BREAKPOINT_DESKTOP]: {
          slidesPerView: 3,
          spaceBetween: 20,
          slidesPerGroup: 3,
        },
      },
      navigation: {
        nextEl: '.swiper-button-next-unique',
        prevEl: '.swiper-button-prev-unique',
      },
      pagination: {
        el: '.swiper-pagination-unique',
        type: 'bullets',
        clickable: true,
      },
    });

    return () => {
      swiper.destroy(true, false);
    };
  }, [data.items]);

  return (
    <div className={`${styleModifiers} ${data.items.length <= 3 ? 'no-swipe' : ''}`}>
      <h2 className="fm-o-variation-switcher__title">{data.title}</h2>

      <div className={`fm-o-variation-switcher__content ${hasGroupId ? 'fm-o-variation-switcher__content--item-selected' : ''}`}>
        <div className="fm-o-variation-switcher__swiper swiper" ref={swiperRef}>
          <div className={`fm-o-variation-switcher__variation-groups swiper-wrapper`}>
            {data.items.map((group) => (
              <div className="swiper-slide">
                <VariationGroup
                  key={group.groupId}
                  group={group}
                  quantityQuestion={data.quantityQuestion}
                  selected={groupId === group.groupId}
                  quantityRemainingText={data.quantityRemainingText}
                  handleVariationChange={handleVariationChange}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="swiper-pagination-unique"></div>

        <div className="swiper-button-prev-unique">
          <Svg iconPath={'#arrow-down'} viewBoxWidth={24} viewBoxHeight={25} />
        </div>
        <div className="swiper-button-next-unique">
          <Svg iconPath={'#arrow-down'} viewBoxWidth={24} viewBoxHeight={25} />
        </div>
      </div>

      {!hasGroupId && (
        <div className="fm-o-variation-switcher__postcode-region">
          <div className="fm-o-variation-switcher__postcode">{data.collectingFromPostcodeText}</div>

          <div className="fm-o-variation-switcher__change-postcode" onClick={() => handleClose(orderFlowCloseReason.changePostCodeOrLocation)}>
            {data.changePostcodeText}
          </div>
        </div>
      )}

      {hasGroupId && hasVariantId && (
        <div className="fm-o-variation-switcher__actions">
          <Button
            text={data.ctaText}
            onClick={handleContinue}
            mainClass="fm-o-variation-switcher__continue-btn"
            styleModifier={['fm-a-button--blue']}
            disabled={
              typeof variationSelected?.quantityRemaining === 'number' &&
              (variationSelected.quantityRemaining < quantity || variationSelected.quantityRemaining === 0)
            }
          />
        </div>
      )}
    </div>
  );
};

export default VariationPicker;
